import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Pencil } from "lucide-react";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  describeTenantRegime,
  resolveTenantLegalForm,
  LEGAL_FORM_LABEL,
  REGIME_LABEL,
} from "@/lib/tenantRegime";
import { getFiscalProfile } from "@/services/fiscalService";
import { type TenantCurrentResponse } from "@/services/authService";

export interface SettingsLegalInfoCardProps {
  tenant: TenantCurrentResponse;
  addressLine: string | null;
  /**
   * Opens the emisor editor. Optional: without it the card is read-only, which
   * is what a role that may read but not write should see.
   *
   * This card used to sit above a SECOND card, "Datos fiscales del emisor",
   * that re-rendered RUT, razón social, dirección, ciudad, departamento,
   * teléfono and correo as inputs — the same seven values twice on one page,
   * one copy read-only and one editable. One card now shows them, and this
   * button opens the editable subset.
   */
  onEdit?: () => void;
}

/**
 * Read-only view of the registered company data.
 *
 * The "Sincronizar datos con DGI" button used to live here and BLIND-wrote
 * legalName, address, city, department, phone and email from GetEntidad. It
 * is gone: SettingsFiscalIdentityCard now offers the same refresh as a
 * per-field comparison you approve, which is the shape the emisor data
 * needs — a blind overwrite is how "0" ended up in nine tenants' domicilio
 * fiscal, and DGI is not always the better source. Two DGI buttons on one
 * page, one safe and one not, was the worse outcome.
 */
export function SettingsLegalInfoCard({
  tenant,
  addressLine,
  onEdit,
}: SettingsLegalInfoCardProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const notProvided = t("settings.legalInfo.notProvided");
  const formattedDate = formatDisplayDate(tenant.createdAt, {
    locale: i18n.language,
  });
  const dateValue = formattedDate === "—" ? notProvided : formattedDate;

  /*
   * The regime genuinely needs the fiscal profile: reading it off
   * company.companyType alone reports every profiled unipersonal as having no
   * regime, which is how an earlier cartera bug made the whole list read the
   * same.
   *
   * This USUALLY issues its own request. SettingsFiscalProfileCard uses the
   * same queryKey, but it lives on a different tab AND is hidden for free
   * plans, so do not count on its cache — the shared key only dedupes when
   * both happen to be mounted. GET /fiscal-profile is auth-only (no plan
   * gate) and returns null when none exists, so a free tenant degrades to the
   * card's "no proporcionado" rather than erroring.
   */
  const { data: profile } = useQuery({
    queryKey: ["fiscal-profile"] as const,
    queryFn: getFiscalProfile,
    retry: false,
  });

  // Legal form and regime are different axes and get their own rows. The
  // accountant's client-detail header collapses them when the regime is
  // Monotributo because two unlabelled badges reading "Unipersonal ·
  // Monotributo" is confusing; labelled rows are not, so both always render.
  const legalForm = resolveTenantLegalForm(
    profile?.companyType,
    tenant.companyType,
  );
  const legalFormValue = legalForm
    ? t(LEGAL_FORM_LABEL[legalForm][0], LEGAL_FORM_LABEL[legalForm][1])
    : "";

  const regime = describeTenantRegime({
    ivaRegime: profile?.ivaRegime ?? null,
    isMonotributo: profile?.taxes?.includes("MONOTRIBUTO") ?? false,
    companyType: tenant.companyType,
  });
  // "unknown" is a gap, not a regime — render the card's own empty treatment
  // rather than inventing a label for it.
  const regimeValue =
    regime === "unknown" ? "" : t(REGIME_LABEL[regime][0], REGIME_LABEL[regime][1]);

  const rows: { label: string; value: string; empty?: boolean }[] = [
    { label: t("settings.legalInfo.rut"), value: tenant.rut },
    {
      label: t("settings.legalInfo.legalName"),
      value: tenant.legalName ?? "",
      empty: !tenant.legalName?.trim(),
    },
    {
      label: t("settings.legalInfo.companyType", "Tipo de empresa"),
      value: legalFormValue,
      empty: !legalFormValue,
    },
    {
      label: t("settings.legalInfo.fiscalRegime", "Régimen fiscal"),
      value: regimeValue,
      empty: !regimeValue,
    },
    // Shown here now that this is the only card: the sucursal lands on every
    // CFE and a value that disagrees with DGI gets comprobantes rejected, so
    // it should be readable without opening the editor.
    {
      label: t("settings.legalInfo.sucursal", "Código de sucursal"),
      value: String(tenant.cdgDGISucur ?? ""),
      empty: tenant.cdgDGISucur == null,
    },
    { label: t("settings.legalInfo.address"), value: addressLine ?? "", empty: !addressLine?.trim() },
    { label: t("settings.legalInfo.phone"), value: tenant.phone ?? "", empty: !tenant.phone?.trim() },
    {
      label: t("settings.legalInfo.contactEmail"),
      value: tenant.contactEmail ?? "",
      empty: !tenant.contactEmail?.trim(),
    },
    { label: t("settings.legalInfo.dateRegistered"), value: dateValue, empty: dateValue === notProvided },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
        <div className="space-y-1.5">
          <CardTitle>{t("settings.legalInfo.title")}</CardTitle>
          <CardDescription>
            {t("settings.legalInfo.description")}
          </CardDescription>
        </div>
        {onEdit ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onEdit}
            className="inline-flex shrink-0 items-center gap-2"
          >
            <Pencil className="h-4 w-4 shrink-0" aria-hidden />
            {t("common.edit", "Editar")}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        <dl className="space-y-3">
          {rows.map(({ label, value, empty }) => (
            <div key={label} className="flex flex-col gap-0.5">
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd
                className={
                  empty
                    ? "text-sm italic text-muted-foreground"
                    : "text-sm text-foreground"
                }
              >
                {empty ? notProvided : value}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
